import { useState, useEffect } from 'react';
import { userApi } from '../api/user.api';
import { ParentChildManager } from '../components/parent-child/ParentChildManager';
import { useAppDispatch } from '../store/hooks';
import { setCredentials } from '../store/slices/authSlice';
import {
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Box,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
} from '@mui/material';
import { AccountCircle, Lock } from '@mui/icons-material';

export const ProfilePage = () => {
  const dispatch = useAppDispatch();
  const [profile, setProfile] = useState<Record<string, unknown>>({});
  const [profileData, setProfileData] = useState({
    bio: '',
    btn: '',
    emergencyContact: '',
    emergencyPhone: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    inactivityTimeout: 10,
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await userApi.getProfile();
      const data = response.data.data;
      setProfile(data);
      if (data.profile) {
        setProfileData({
          bio: data.profile.bio || '',
          btn: data.profile.btn || '',
          emergencyContact: data.profile.emergencyContact || '',
          emergencyPhone: data.profile.emergencyPhone || '',
          address: data.profile.address || '',
          city: data.profile.city || '',
          country: data.profile.country || '',
          postalCode: data.profile.postalCode || '',
          inactivityTimeout: data.profile.inactivityTimeout ?? 10,
        });
      }
    } catch (err: unknown) {
      console.error('Profile fetch error:', err);
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string }; status?: number } };
        const errorMessage = axiosError.response?.data?.message ||
                           `Failed to load profile (${axiosError.response?.status || 'unknown error'})`;
        setError(errorMessage);
      } else {
        setError('Failed to load profile');
      }
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await userApi.updateProfile(profileData);
      setSuccess('Profile updated successfully');

      // Fetch updated profile
      const response = await userApi.getProfile();
      const updatedProfile = response.data.data;
      setProfile(updatedProfile);

      // Update auth state with new profile data
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      if (accessToken && refreshToken) {
        dispatch(setCredentials({
          user: updatedProfile,
          accessToken,
          refreshToken,
        }));
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Update failed');
      } else {
        setError('Update failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await userApi.changePassword(passwordData.currentPassword, passwordData.newPassword);
      setSuccess('Password changed successfully');
      setPasswordDialogOpen(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Password change failed');
      } else {
        setError('Password change failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        My Profile
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ width: 80, height: 80, mr: 2 }}>
            <AccountCircle sx={{ fontSize: 80 }} />
          </Avatar>
          <Box>
            <Typography variant="h5">
              {profile.firstName as string} {profile.lastName as string}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {profile.email as string}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Role: {(profile.role as string)?.replace('_', ' ')}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box component="form" onSubmit={handleProfileUpdate}>
          <Typography variant="h6" gutterBottom>
            Personal Information
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bio"
                multiline
                rows={3}
                value={profileData.bio}
                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
              />
            </Grid>
            {profile.role === 'PLAYER' && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    label="British Tennis Number (BTN)"
                    value={profileData.btn}
                    onChange={(e) => setProfileData({ ...profileData, btn: e.target.value })}
                    helperText="Your BTN from the LTA"
                  />
                  {profileData.btn && (
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mt: 1, minWidth: 'auto', whiteSpace: 'nowrap' }}
                      onClick={() => {
                        window.open(
                          `https://competitions.lta.org.uk/find/player?q=${encodeURIComponent(profileData.btn)}`,
                          '_blank',
                          'noopener,noreferrer'
                        );
                      }}
                    >
                      View on LTA
                    </Button>
                  )}
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact"
                value={profileData.emergencyContact}
                onChange={(e) =>
                  setProfileData({ ...profileData, emergencyContact: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Phone"
                value={profileData.emergencyPhone}
                onChange={(e) =>
                  setProfileData({ ...profileData, emergencyPhone: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={profileData.address}
                onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={profileData.city}
                onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Country"
                value={profileData.country}
                onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Postal Code"
                value={profileData.postalCode}
                onChange={(e) => setProfileData({ ...profileData, postalCode: e.target.value })}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Security Settings
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Inactivity Timeout (minutes)"
                value={profileData.inactivityTimeout}
                onChange={(e) => setProfileData({
                  ...profileData,
                  inactivityTimeout: Math.max(1, parseInt(e.target.value) || 10)
                })}
                inputProps={{ min: 1, max: 120 }}
                helperText="You'll be logged out after this many minutes of inactivity"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Lock />}
              onClick={() => setPasswordDialogOpen(true)}
            >
              Change Password
            </Button>
          </Box>
        </Box>
      </Paper>

      {(profile.role === 'PARENT' || profile.role === 'PLAYER') && (
        <Box sx={{ mt: 3 }}>
          <ParentChildManager
            userId={profile.id as string}
            userRole={profile.role as 'PARENT' | 'PLAYER'}
            isEditable={true}
          />
        </Box>
      )}

      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handlePasswordChange}>
          <DialogTitle>Change Password</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, currentPassword: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, newPassword: e.target.value })
                  }
                  required
                  helperText="Min 8 characters, must include uppercase, lowercase, number, and special character"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  required
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPasswordDialogOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};
